import { useState, useEffect, useRef } from "react"
import { toast } from "sonner"
import { Link, useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { AxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/modules/shared/ui/components/card"
import { Input } from "@/modules/shared/ui/components/input"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { apiClient as api } from "@/lib/api-client"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { GoogleAuthButton } from "@/modules/auth/shared/components/GoogleAuthButton"
import { FetchCurrentUserAsync } from "@/modules/auth/infrastructure/auth-service"
import { restoreAuth } from "@/modules/auth/store/authSlice"
import { useDispatch } from "react-redux"
import { broadcastLogin } from "@/lib/session-sync"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"

const formSchema = z
  .object({
    email: z.email({
      message: "Please enter a valid email address.",
    }),
    password: z
      .string()
      .min(1, "Please enter your password.")
      .min(8, "Password must be at least 8 characters.")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter.")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter.")
      .regex(/[0-9]/, "Password must contain at least one number.")
      .regex(
        /[!@#$%^&*]/,
        "Password must contain at least one special character (!@#$%^&*)."
      ),
    confirmPassword: z.string().min(1, {
      message: "Please enter your password.",
    }),
    agreedTerms: z.boolean().refine((val) => val === true, {
      message: "You must agree to the Terms & Conditions.",
    }),
    agreedPrivacy: z.boolean().refine((val) => val === true, {
      message: "You must agree to the Privacy Policy.",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match.",
    path: ["confirmPassword"],
  })

type FormValues = z.infer<typeof formSchema>

export default function SignupForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [legalDocs, setLegalDocs] = useState<{ terms?: { version: string }, privacy?: { version: string } } | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { signupWithCredentials } = useAuth()
  const { getToken } = useRecaptcha()

  useEffect(() => {
    if (!cooldownUntil) return
    const tick = () => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null)
        if (cooldownRef.current) clearInterval(cooldownRef.current)
      }
    }
    tick()
    cooldownRef.current = setInterval(tick, 1000)
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current)
    }
  }, [cooldownUntil])

  const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil

  useEffect(() => {
    async function fetchDocs() {
      try {
        const response = await api.get('/legal-documents');
        setLegalDocs(response.data);
      } catch (err) {
        console.error("Failed to fetch legal documents", err);
      }
    }
    fetchDocs();
  }, []);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      agreedTerms: false,
      agreedPrivacy: false,
    },
  })

  const handleSubmit = async (values: FormValues) => {
    if (!legalDocs?.terms?.version || !legalDocs?.privacy?.version) {
      toast.error("Legal document versions are not loaded. Please refresh.");
      return;
    }
    try {
      const captchaToken = await getToken("signup")
      await signupWithCredentials(
        values.email,
        values.password,
        legalDocs.terms.version,
        legalDocs.privacy.version,
        captchaToken
      );
      sessionStorage.setItem("signupEmail", values.email)
      navigate("/otp")
    } catch (err: unknown) {
      const axiosErr = err as AxiosError
      if (axiosErr?.response?.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
        const retryAfter = parseInt(
          (axiosErr.response.headers as Record<string, string>)?.['retry-after'] ?? '60',
          10
        )
        setCooldownUntil(Date.now() + retryAfter * 1000)
        return
      }
    }
  }

  return (
    <Card className="shadow-none border-0 md:w-[75%] w-full">
      <CardHeader>
        <CardTitle className="text-3xl text-bold">Create your account</CardTitle>
        <CardDescription className="text-base" >
          Sign up to your account to manage employees
        </CardDescription>
      </CardHeader>

      <CardContent className="pb-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    Email<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      className="border-border focus:border-blue-500 focus:ring-blue-500"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">
                    Password<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-foreground!">
                    Confirm Password<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        className="pr-10 border-border focus:border-blue-500 focus:ring-blue-500"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-red-500" />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2 mt-4 mb-4">
              <FormField
                control={form.control}
                name="agreedTerms"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-medium leading-none cursor-pointer">
                      I agree to the{" "}
                      <a href="/legal/terms" target="_blank" className="text-[#40C4FF] hover:underline whitespace-nowrap">
                        Terms & Conditions
                      </a>
                    </FormLabel>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="agreedPrivacy"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-medium leading-none cursor-pointer">
                      I agree to the{" "}
                      <a href="/legal/privacy" target="_blank" className="text-[#40C4FF] hover:underline whitespace-nowrap">
                        Privacy Policy
                      </a>
                    </FormLabel>
                    <FormMessage className="text-red-500" />
                  </FormItem>
                )}
              />
            </div>

            <Button
              id="signup-button"
              type="submit"
              className="w-full text-white py-6 text-base"
              style={{ backgroundColor: "#1A237E" }}
              disabled={form.formState.isSubmitting || isCoolingDown}
            >
              {isCoolingDown
                ? "Try again later"
                : form.formState.isSubmitting
                  ? "Creating account..."
                  : "Sign Up"}
            </Button>
          </form>
        </Form>
      </CardContent>

      <CardFooter className="flex flex-col gap-4">
        <div className="text-center text-muted-foreground text-sm w-full">
          Or continue with
        </div>

        <div className="flex justify-center gap-3 w-full">
          <div className="w-12 h-12 flex items-center justify-center rounded-full overflow-hidden">
            <GoogleAuthButton
              onSuccess={async (idToken: string) => {
                await api.post("/auth/external-login", { token: idToken });

                const userProfile = await FetchCurrentUserAsync();

                dispatch(restoreAuth({
                  user: {
                    user: userProfile,
                    roles: userProfile.roles,
                    permissions: userProfile.permissions,
                    currentOrganizationId: userProfile.currentOrganizationId || null,
                  },
                  loginTimestamp: Date.now()
                }));
                broadcastLogin(userProfile.email ?? "");
                navigate("/");
              }}
            />
          </div>
        </div>

        <p className="text-center text-muted-foreground text-sm w-full">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[#40C4FF]">
            Log in
          </Link>
        </p>
      </CardFooter>
    </Card>
  )
}
